/**
 * 프로젝트 허브 (목록 + 타임라인).
 * - 인라인 작업 추가: 입력 후 Enter → 이 프로젝트에 오늘 날짜로 생성, 연속 입력
 * - 행 완료 체크 / 바 드래그 이동·리사이즈 → 히스토리 스택 (Ctrl+Z) + 소프트 갱신
 * - 선행 관계 연결선 (SVG 엘보, 충돌은 빨간 점선)
 * - 행 클릭 → 일정 패널 (calendar.js 의 openCalendarEvent)
 * 리스너는 #projHub 에 위임돼 있어 innerHTML 갱신 뒤에도 살아 있다.
 */
import { showContextMenu } from './context-menu.js';
import { api, createCommand, patchCommand, composite, commitQuiet, recordQuiet, showToast, labels, fmtCount, refreshGrid, beginDrag, endDrag } from './calendar-history.js';
import { bindQuickTime } from './quick-time.js';
import { splitScope, sendScoped } from './calendar-scope.js';

function localISODate(d) {
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}
function addDays(ds, n) {
    const d = new Date(ds + 'T00:00:00');
    d.setDate(d.getDate() + n);
    return localISODate(d);
}
function diffDays(a, b) {
    return Math.round((new Date(b + 'T00:00:00') - new Date(a + 'T00:00:00')) / 86400000);
}

export function setupProjectTimeline() {
    const hub = document.getElementById('projHub');
    if (!hub || hub.dataset.tlBound) return; // 뷰 전환으로 되살아난 루트는 이미 묶여 있다
    hub.dataset.tlBound = '1';
    const tlEl = () => document.getElementById('calpTl');

    // ── 연결선 ──
    function drawDeps() {
        const tl = tlEl();
        const svg = document.getElementById('calpDepSvg');
        if (!tl || !svg) return;
        let deps = [];
        try { deps = JSON.parse(tl.dataset.deps || '[]'); } catch (e) { deps = []; }
        if (!Array.isArray(deps)) deps = []; // 서버는 선행 관계가 없으면 "null" 을 내려준다
        while (svg.firstChild) svg.removeChild(svg.firstChild);
        if (!deps.length) return;
        const box = tl.getBoundingClientRect();
        const byID = {};
        tl.querySelectorAll('.calp-bar[data-eid]').forEach((b) => { byID[b.dataset.eid] = b; });
        deps.forEach((dp) => {
            const from = byID[dp.d], to = byID[dp.e];
            if (!from || !to) return;
            const fr = from.getBoundingClientRect(), tr = to.getBoundingClientRect();
            const x1 = fr.right - box.left, y1 = fr.top + fr.height / 2 - box.top;
            const x2 = tr.left - box.left, y2 = tr.top + tr.height / 2 - box.top;
            const conflict = to.dataset.start <= from.dataset.end;
            const midX = Math.max(x1 + 8, Math.min(x2 - 8, x1 + 12));
            const d = 'M' + x1 + ' ' + y1 + ' H' + midX + ' V' + y2 + ' H' + (x2 - 4);
            // 넓은 투명 경로 = 클릭 대상 (선행 관계 제거 메뉴)
            const hit = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            hit.setAttribute('d', d);
            hit.setAttribute('class', 'hit');
            hit.dataset.e = dp.e; hit.dataset.d = dp.d;
            svg.appendChild(hit);
            const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path.setAttribute('d', d);
            if (conflict) path.setAttribute('class', 'conflict');
            svg.appendChild(path);
            const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
            arrow.setAttribute('points', (x2 - 4) + ',' + (y2 - 4) + ' ' + x2 + ',' + y2 + ' ' + (x2 - 4) + ',' + (y2 + 4));
            if (conflict) arrow.setAttribute('class', 'conflict');
            svg.appendChild(arrow);
        });
    }
    drawDeps();
    // 연결선 클릭/우클릭 → 선행 관계 제거
    function depMenu(hit, x, y) {
        const target = hub.querySelector('.calp-row[data-id="' + hit.dataset.e + '"]');
        const from = hub.querySelector('.calp-row[data-id="' + hit.dataset.d + '"]');
        showContextMenu(x, y, [
            { label: (from ? from.dataset.title : '?') + ' → ' + (target ? target.dataset.title : '?'), disabled: true, onClick: () => {} },
            { separator: true },
            { label: hub.dataset.depsRemove || '선행 관계 제거', danger: true, onClick: () => { if (!target) return; const before = target.dataset.deps || ''; const after = before.split(',').filter((v) => v && v !== hit.dataset.d).join(','); target.dataset.deps = after; commitQuiet(patchCommand(target.dataset.id, { dep_ids: before }, { dep_ids: after })); } },
        ]);
    }
    hub.addEventListener('contextmenu', (e) => { const hit = e.target.closest && e.target.closest('#calpDepSvg path.hit'); if (hit) { e.preventDefault(); e.stopPropagation(); depMenu(hit, e.clientX, e.clientY); } }, true);
    hub.addEventListener('click', (e) => { const hit = e.target.closest && e.target.closest('#calpDepSvg path.hit'); if (hit) { e.preventDefault(); depMenu(hit, e.clientX, e.clientY); } });
    // 바 오른쪽 손잡이를 끌어 다른 바에 놓으면 선행 → 후행 관계가 생긴다
    let link = null;
    hub.addEventListener('mousedown', (e) => {
        const h = e.target.closest('.calp-link-h');
        if (!h || e.button !== 0) return;
        const bar = h.closest('.calp-bar'), svg = document.getElementById('calpDepSvg'), tl = tlEl();
        if (!bar || !svg || !tl) return;
        e.preventDefault(); e.stopPropagation();
        const box = tl.getBoundingClientRect(), r = bar.getBoundingClientRect();
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('class', 'linking');
        svg.appendChild(path);
        link = { fromId: bar.dataset.eid, x1: r.right - box.left, y1: r.top + r.height / 2 - box.top, path, box, target: null };
        document.body.classList.add('calp-linking');
        beginDrag();
    }, true);
    document.addEventListener('mousemove', (e) => {
        if (!link) return;
        const x2 = e.clientX - link.box.left, y2 = e.clientY - link.box.top;
        link.path.setAttribute('d', 'M' + link.x1 + ' ' + link.y1 + ' H' + (link.x1 + 10) + ' V' + y2 + ' H' + x2);
        const el = document.elementFromPoint(e.clientX, e.clientY);
        const bar = el && el.closest ? el.closest('.calp-bar[data-eid]') : null;
        const t = bar && bar.dataset.eid !== link.fromId ? bar : null;
        if (link.target && link.target !== t) link.target.classList.remove('link-target');
        if (t) t.classList.add('link-target');
        link.target = t;
    });
    document.addEventListener('mouseup', () => {
        if (!link) return;
        const l = link; link = null;
        l.path.remove();
        document.body.classList.remove('calp-linking');
        endDrag();
        if (!l.target) return;
        l.target.classList.remove('link-target');
        const row = hub.querySelector('.calp-row[data-id="' + l.target.dataset.eid + '"]');
        if (!row) return;
        const before = row.dataset.deps || '';
        const ids = before.split(',').filter(Boolean);
        if (ids.includes(l.fromId)) return;
        const after = ids.concat(l.fromId).join(',');
        row.dataset.deps = after;
        commitQuiet(patchCommand(row.dataset.id, { dep_ids: before }, { dep_ids: after }));
    });
    let rt;
    window.addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(drawDeps, 150); });
    document.addEventListener('calendar:refreshed', () => {
        drawDeps();
        bindQuick();
    });

    // ── 인라인 작업 추가 ──
    function bindQuick() {
        const input = document.getElementById('calpQuickInput');
        if (!input || input.dataset.bound) return;
        input.dataset.bound = '1';
        // 날짜 감지 (AZ2): 표의 새 작업은 날짜 미정이라 시각만으로는 붙일 곳이 없다 — 날짜·반복이
        // 보일 때만 칩이 뜨고, 그러면 그 날짜의 작업(반복이면 시리즈)이 된다.
        const H = hub.dataset;
        const chip = document.getElementById('calpQuickChip');
        const qt = chip ? bindQuickTime(input, chip, { offLabel: H.quickTimeOff, today: H.today || '', needDate: true, weekdays: H.weekdays, fromFmt: H.quickFrom, weekStart: H.weekStart }) : null;
        input.addEventListener('keydown', (e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            const title = input.value.trim();
            if (e.ctrlKey || e.metaKey) {
                openCreate(title);
                return;
            }
            if (!title) return;
            const body = new URLSearchParams();
            body.set('title', title);
            body.set('start_date', ''); // 미정
            body.set('end_date', '');
            body.set('all_day', 'on');
            body.set('color', hub.dataset.calColor || 'c1');
            body.set('calendar_id', hub.dataset.calId);
            if (qt) qt.applyTo(body);
            input.disabled = true;
            api.create(body)
                .then((ev) => {
                    if (ev && ev.series) { // 반복: 시리즈 생성은 실행 취소 밖 (패널 저장과 같은 규칙)
                        showToast(fmtCount(H.msgSeriesCreated || '반복 일정 %d개를 만들었습니다', ev.created_count || 1));
                        return refreshGrid();
                    }
                    return recordQuiet(createCommand(ev.id), { refresh: 'now' });
                })
                .then(() => {
                    const again = document.getElementById('calpQuickInput');
                    if (again) {
                        again.value = '';
                        again.focus();
                    }
                })
                .catch(() => {
                    input.disabled = false;
                    showToast(labels().failed, { error: true });
                });
        });
    }
    bindQuick();

    function openCreate(title) {
        if (!window.openCalendarCreate) return;
        window.openCalendarCreate(null, null); // 날짜 미정
        const sel = document.getElementById('calCalSel');
        if (sel) {
            sel.value = hub.dataset.calId;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
        }
        const t = document.getElementById('calTitle');
        if (t && title) t.value = title;
    }
    const addBtn = document.getElementById('calpAddEvent');
    if (addBtn) addBtn.addEventListener('click', () => openCreate(''));

    // ── 드래그: 바 이동 / 가장자리 리사이즈 (타임라인 + 목록의 미니 트랙) ──
    // 스팬 정보는 #projHub(dataset) 에 있고, 트랙 폭 = 스팬 전체 일수라 px→일수 환산이 선형.
    function span() {
        const tl = tlEl();
        const days = Number(hub.dataset.spanDays || (tl && tl.dataset.spanDays)) || 0;
        const start = hub.dataset.spanStart || (tl && tl.dataset.spanStart) || '';
        return { days, start };
    }
    function rowOf(el) {
        return el.closest('.calp-row[data-id]');
    }
    // 여러 행이 선택돼 있고 그중 하나를 끌면 전부 같이 움직인다.
    function moveTargets(bar) {
        const row = rowOf(bar);
        if (row && row.classList.contains('selected')) {
            return Array.from(hub.querySelectorAll('.calp-row.selected[data-id]'));
        }
        return row ? [row] : [];
    }
    let drag = null;
    let swallowClick = false;
    // 드래그 중 날짜 툴팁
    let tip = null;
    function showTip(x, y, text) {
        if (!tip) { tip = document.createElement('div'); tip.className = 'calp-drag-tip'; document.body.appendChild(tip); }
        tip.textContent = text;
        tip.style.left = (x + 14) + 'px';
        tip.style.top = (y - 30) + 'px';
    }
    function hideTip() { if (tip) { tip.remove(); tip = null; } }
    function dayAtX(track, x, sp) {
        const r = track.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (x - r.left) / r.width));
        return addDays(sp.start, Math.min(sp.days - 1, Math.floor(ratio * sp.days)));
    }
    hub.addEventListener('mousedown', (e) => {
        if (e.button !== 0) return;
        const sp = span();
        if (!sp.days) return;
        const bar = e.target.closest('.calp-bar');
        const track = e.target.closest('.calp-track');
        if (!bar && track && rowOf(track)) {
            // 빈 트랙에서 드래그 → 그 기간으로 설정
            const r0 = rowOf(track);
            drag = { mode: 'set', track, row: r0, sp, from: dayAtX(track, e.clientX, sp), to: null, moved: false, start: r0.dataset.start || '', end: r0.dataset.end || '' };
            beginDrag();
            e.preventDefault();
            return;
        }
        if (!bar) return;
        const r = bar.getBoundingClientRect();
        const edge = 7;
        let mode = 'move';
        if (e.clientX - r.left <= edge) mode = 'resize-l';
        else if (r.right - e.clientX <= edge) mode = 'resize-r';
        const trackRect = bar.parentElement.getBoundingClientRect();
        drag = { mode, sp, bar, targets: moveTargets(bar), x0: e.clientX, start: bar.dataset.start, end: bar.dataset.end, trackW: trackRect.width, moved: false, delta: 0 };
        beginDrag();
        e.preventDefault();
    });
    function previewBar(bar, start, end, sp) {
        const dayPct = 100 / sp.days;
        bar.style.left = (diffDays(sp.start, start) * dayPct) + '%';
        bar.style.width = ((diffDays(start, end) + 1) * dayPct) + '%';
    }
    document.addEventListener('mousemove', (e) => {
        if (!drag) return;
        if (drag.mode === 'set') {
            const to = dayAtX(drag.track, e.clientX, drag.sp);
            if (to === drag.to) return;
            drag.to = to;
            drag.moved = true;
            let pv = drag.track.querySelector('.calp-set-preview');
            if (!pv) {
                pv = document.createElement('span');
                pv.className = 'calp-set-preview';
                drag.track.appendChild(pv);
            }
            const lo = drag.from < to ? drag.from : to, hi = drag.from < to ? to : drag.from;
            previewBar(pv, lo, hi, drag.sp);
            showTip(e.clientX, e.clientY, lo + ' → ' + hi);
            return;
        }
        const dx = e.clientX - drag.x0;
        const delta = Math.round(dx / drag.trackW * drag.sp.days);
        if (Math.abs(dx) > 3) drag.moved = true;
        if (delta === drag.delta) return;
        drag.delta = delta;
        drag.bar.classList.add('dragging');
        if (drag.mode === 'move') {
            drag.targets.forEach((row) => {
                const b = row.querySelector('.calp-bar[data-eid]');
                if (b) { b.classList.add('dragging'); previewBar(b, addDays(b.dataset.start, delta), addDays(b.dataset.end, delta), drag.sp); }
            });
            showTip(e.clientX, e.clientY, addDays(drag.start, delta) + ' → ' + addDays(drag.end, delta));
        } else if (drag.mode === 'resize-r') {
            let end = addDays(drag.end, delta);
            if (end < drag.start) end = drag.start;
            previewBar(drag.bar, drag.start, end, drag.sp);
            showTip(e.clientX, e.clientY, drag.start + ' → ' + end);
        } else {
            let start = addDays(drag.start, delta);
            if (start > drag.end) start = drag.end;
            previewBar(drag.bar, start, drag.end, drag.sp);
            showTip(e.clientX, e.clientY, start + ' → ' + drag.end);
        }
        drawDeps();
    });
    function resetPreview(d) {
        hub.querySelectorAll('.calp-bar.dragging').forEach((b) => { b.classList.remove('dragging'); b.style.left = ''; b.style.width = ''; });
        hub.querySelectorAll('.calp-set-preview').forEach((p) => p.remove());
        void d;
        drawDeps();
    }
    document.addEventListener('mouseup', () => {
        if (!drag) return;
        const d = drag;
        drag = null;
        endDrag();
        hideTip();
        // 반복 인스턴스는 놓는 순간 범위를 묻는다 (BA1): 「이 일정만」은 예전처럼 낙관적으로 칠하고
        // 실행 취소 스택으로, 「이후 모두」·「전체」는 반복 행만 series_scope 로(실행 취소 밖), 취소면 제자리로.
        if (d.mode === 'set') {
            const pv = d.track.querySelector('.calp-set-preview');
            if (!d.moved || !d.to) { if (pv) pv.remove(); return; }
            swallowClick = true;
            const lo = d.from < d.to ? d.from : d.to, hi = d.from < d.to ? d.to : d.from;
            const row = d.row;
            splitScope([row], 'edit').then((sp) => {
                if (pv) pv.remove();
                if (!sp) return;
                if (sp.scoped.length) { sendScoped(sp.scoped, sp.scope, () => ({ start_date: lo, end_date: hi })); return; }
                const bar = row.querySelector('.calp-bar[data-eid]');
                if (bar) { previewBar(bar, lo, hi, d.sp); bar.dataset.start = lo; bar.dataset.end = hi; }
                row.dataset.start = lo; row.dataset.end = hi;
                const sb = row.querySelector('.calp-when-btn[data-which="start"]'), eb = row.querySelector('.calp-when-btn[data-which="end"]');
                if (sb) { sb.textContent = lo; sb.classList.remove('tbd'); }
                if (eb) { eb.textContent = hi; eb.classList.remove('tbd'); }
                if (!bar) { const nb = document.createElement('span'); nb.className = 'calp-bar cal-' + (row.dataset.color || 'c1') + ' active'; nb.dataset.eid = row.dataset.id; nb.dataset.start = lo; nb.dataset.end = hi; d.track.appendChild(nb); previewBar(nb, lo, hi, d.sp); }
                commitQuiet(patchCommand(row.dataset.id, { start_date: d.start, end_date: d.end }, { start_date: lo, end_date: hi }));
            });
            return;
        }
        if (!d.moved || d.delta === 0) { resetPreview(d); return; }
        swallowClick = true;
        // 바뀔 행과 새 날짜를 먼저 모은다 (칠하기는 답을 들은 뒤)
        const changes = [];
        if (d.mode === 'move') {
            d.targets.forEach((row) => {
                const b = row.querySelector('.calp-bar[data-eid]');
                if (!b) return;
                changes.push({ row, bar: b, before: { start_date: b.dataset.start, end_date: b.dataset.end }, after: { start_date: addDays(b.dataset.start, d.delta), end_date: addDays(b.dataset.end, d.delta) } });
            });
        } else {
            let start = d.start, end = d.end;
            if (d.mode === 'resize-r') { end = addDays(d.end, d.delta); if (end < d.start) end = d.start; }
            else { start = addDays(d.start, d.delta); if (start > d.end) start = d.end; }
            const row = rowOf(d.bar);
            changes.push({ row: row || d.bar, bar: d.bar, id: d.bar.dataset.eid, before: { start_date: d.start, end_date: d.end }, after: { start_date: start, end_date: end } });
        }
        const byEl = new Map(changes.map((ch) => [ch.row, ch]));
        splitScope(changes.map((ch) => ch.row), 'edit').then((sp) => {
            if (!sp) { resetPreview(d); return; }
            const cmds = sp.plain.map((row) => {
                const ch = byEl.get(row);
                const ns = ch.after.start_date, ne = ch.after.end_date;
                ch.bar.classList.remove('dragging');
                ch.bar.dataset.start = ns; ch.bar.dataset.end = ne;
                if (row.dataset) { row.dataset.start = ns; row.dataset.end = ne; }
                const txt = row.querySelector && row.querySelector('.calp-when-text');
                if (txt) txt.textContent = ns + (ne !== ns ? ' → ' + ne : '');
                return patchCommand(ch.id || row.dataset.id, ch.before, ch.after);
            });
            drawDeps();
            if (cmds.length) commitQuiet(cmds.length === 1 ? cmds[0] : composite(cmds));
            if (sp.scoped.length) sendScoped(sp.scoped, sp.scope, (row) => byEl.get(row).after);
        });
    });
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || !drag) return;
        const d = drag;
        drag = null;
        endDrag();
        hideTip();
        resetPreview(d);
        swallowClick = true;
        e.stopImmediatePropagation();
    }, true);
    hub.addEventListener('mousemove', (e) => {
        const bar = e.target.closest('.calp-bar');
        if (!bar || drag) return;
        const r = bar.getBoundingClientRect();
        bar.style.cursor = (e.clientX - r.left <= 7 || r.right - e.clientX <= 7) ? 'ew-resize' : 'grab';
    });

    // ── 행 클릭 → 패널 / 완료 체크 (위임) ──
    hub.addEventListener('click', (e) => {
        if (swallowClick) {
            swallowClick = false;
            e.stopPropagation();
            e.preventDefault();
            return;
        }
        if (e.target.closest('.calp-done, .calp-sel, a, button, input, .calp-quick, .calp-track, .calp-grip, .calp-td-prop')) return;
        const row = e.target.closest('.calp-row[data-id]');
        if (row && window.openCalendarEvent) window.openCalendarEvent(row);
    });
    hub.addEventListener('change', (e) => {
        const cb = e.target.closest('.calp-done');
        if (!cb || !cb.closest('.calp-tl')) return; // 표 행의 링은 project-list.js 가 맡는다
        const row = cb.closest('.calp-row');
        const wasDone = row.dataset.done === '1';
        // 낙관적 반영: 즉시 완료 표시(링·취소선·상태 배지), 서버는 뒤에서
        row.dataset.done = wasDone ? '0' : '1';
        row.classList.toggle('is-done', !wasDone);
        const st = row.querySelector('.calp-status');
        if (st) {
            if (!wasDone) { st.dataset.prevText = st.textContent; st.dataset.prevClass = st.className; st.textContent = (document.getElementById('calendarModal') || { dataset: {} }).dataset.lDone || '완료'; st.className = 'calp-status done'; }
            else if (st.dataset.prevText) { st.textContent = st.dataset.prevText; st.className = st.dataset.prevClass || 'calp-status active'; }
        }
        commitQuiet(patchCommand(row.dataset.id, { done: wasDone ? '1' : '0' }, { done: wasDone ? '0' : '1' }));
    });
    // 저장 상태 표시 (헤더)
    const stateEl = document.getElementById('calpSaveState');
    let stateTimer = null;
    document.addEventListener('calendar:save-state', (e) => {
        if (!stateEl) return;
        const kind = e.detail;
        stateEl.textContent = stateEl.dataset[kind] || '';
        stateEl.classList.toggle('err', kind === 'failed');
        stateEl.classList.add('show');
        clearTimeout(stateTimer);
        if (kind !== 'saving') stateTimer = setTimeout(() => stateEl.classList.remove('show'), 1500);
    });
}
